<template>
	<v-container class="dashboard-container">
		<v-row class="contentRow">
			<v-col cols="12">
				<div class="steps-wrapper">
					<!-- Step 1 -->
					<div 
						class="step-container clickable-step"
						@click="!isStepGrayed('step1') && handleWorkflowStepClick('step1')"
						:class="{ 
							'step-active': activeStep === 'step1',
							'tutorial-grayed': isStepGrayed('step1')
						}">
						<div class="step-circle">
							<v-icon size="35" color="trace">fas fa-scroll</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Policy</b></h4>
							<h5>Goals, commitments, and accepted standards</h5>
						</div>
					</div>

					<!-- Arrow 1 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 2 -->
					<div class="step-container clickable-step"
						@click="!isStepGrayed('step2') && handleWorkflowStepClick('step2')"
						:class="{ 
							'step-active': activeStep === 'step2',
							'tutorial-grayed': isStepGrayed('step2')
						}">
						<div class="step-circle">
							<v-icon size="35" color="success">fas fa-cogs</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Process & Roles</b></h4>
							<h5>Critical company processes and responsibilities</h5>
						</div>
					</div>

					<!-- Arrow 2 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 3 -->
					<div class="step-container clickable-step" 
						@click="!isStepGrayed('step3') && handleWorkflowStepClick('step3')"
						:class="{ 
							'step-active': activeStep === 'step3',
							'tutorial-grayed': isStepGrayed('step3')
						}">
						<div class="step-circle">
							<v-icon size="35" color="error">fas fa-triangle-exclamation</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Risk</b></h4>
							<h5>What can affect the processes</h5>
						</div>
					</div>

					<!-- Arrow 3 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 4 -->
					<div class="step-container clickable-step"
						@click="!isStepGrayed('step4') && handleWorkflowStepClick('step4')"
						:class="{ 
							'step-active': activeStep === 'step4',
							'tutorial-grayed': isStepGrayed('step4')
						}">
						<div class="step-circle">
							<v-icon size="35" color="warning">fas fa-map</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Plan</b></h4>
							<h5>Actions to mitigate the risks</h5>
						</div>
					</div>

					<!-- Arrow 4 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 5 -->
					<div class="step-container clickable-step"
						@click="!isStepGrayed('step5') && handleWorkflowStepClick('step5')"
						:class="{ 
							'step-active': activeStep === 'step5',
							'tutorial-grayed': isStepGrayed('step5')
						}">
						<div class="step-circle">
							<v-icon size="35" color="primary">fas fa-magnifying-glass-chart</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Management Review</b></h4>
							<h5>Evaluate effectiveness and set priorities</h5>
						</div>
					</div>
				</div>

				<v-card v-if="activeStep === 'step2' && hasSavedProcesses" class="saved-processes-section pa-5 mt-10" outlined>
					<template v-if="savedMainProcesses.length > 0">
						<div class="saved-section-header">
							<h2 class="saved-section-title">Main Processes</h2>
							<div>
								<v-btn v-if="!reorderingMain" small outlined color="primary" @click="startReorder('main')">
									<v-icon small left>fas fa-arrows-left-right</v-icon>
									Reorder Main Processes
								</v-btn>
								<template v-else>
									<v-btn
										small
										outlined
										color="success"
										class="mr-2"
										:loading="savingMainOrder"
										@click="saveOrder('main')"
									>
										Save Order
									</v-btn>
									<v-btn small outlined color="error" :disabled="savingMainOrder" @click="cancelReorder('main')">
										Cancel
									</v-btn>
								</template>
							</div>
						</div>

						<p v-if="reorderingMain" class="drag-hint">
							<v-icon x-small>fas fa-circle-info</v-icon> Drag cards by the handle to reorder, then Save Order.
						</p>
						<draggable
							v-model="savedMainProcesses"
							tag="div"
							class="saved-process-grid"
							handle=".drag-handle"
							:disabled="!reorderingMain"
							ghost-class="drag-ghost"
							animation="150"
						>
							<div
								v-for="row in savedMainProcesses"
								:key="row.ID"
								class="saved-process-card"
								:class="{ 'drag-enabled': reorderingMain, 'clickable-card': !reorderingMain }"
								@click="!reorderingMain && openStepsDialog(row)"
							>
								<div v-if="reorderingMain" class="drag-handle" title="Drag to reorder" @click.stop>
									<v-icon small>fas fa-grip-lines</v-icon>
								</div>

								<p class="field-label">Process name</p>
								<p class="field-value">{{ getProcessField(row, 'name') }}</p>

								<p class="field-label">Owner</p>
								<p class="field-value">{{ getProcessField(row, 'owner') }}</p>

								<p class="field-label">Input</p>
								<p class="field-value">{{ getProcessField(row, 'input') }}</p>

								<p class="field-label">Output</p>
								<p class="field-value">{{ getProcessField(row, 'output') }}</p>
							</div>
						</draggable>
					</template>

					<template v-if="savedSubProcesses.length > 0">
						<div class="saved-section-header" :class="{ 'mt-8': savedMainProcesses.length > 0 }">
							<h2 class="saved-section-title">Sub Processes</h2>
							<div>
								<v-btn v-if="!reorderingSub" small outlined color="primary" @click="startReorder('sub')">
									<v-icon small left>fas fa-arrows-left-right</v-icon>
									Reorder Sub Processes
								</v-btn>
								<template v-else>
									<v-btn
										small
										outlined
										color="success"
										class="mr-2"
										:loading="savingSubOrder"
										@click="saveOrder('sub')"
									>
										Save Order
									</v-btn>
									<v-btn small outlined color="error" :disabled="savingSubOrder" @click="cancelReorder('sub')">
										Cancel
									</v-btn>
								</template>
							</div>
						</div>

						<p v-if="reorderingSub" class="drag-hint">
							<v-icon x-small>fas fa-circle-info</v-icon> Drag cards by the handle to reorder, then Save Order.
						</p>
						<draggable
							v-model="savedSubProcesses"
							tag="div"
							class="saved-process-grid"
							handle=".drag-handle"
							:disabled="!reorderingSub"
							ghost-class="drag-ghost"
							animation="150"
						>
							<div
								v-for="row in savedSubProcesses"
								:key="row.ID"
								class="saved-process-card"
								:class="{ 'drag-enabled': reorderingSub, 'clickable-card': !reorderingSub }"
								@click="!reorderingSub && openStepsDialog(row)"
							>
								<div v-if="reorderingSub" class="drag-handle" title="Drag to reorder" @click.stop>
									<v-icon small>fas fa-grip-lines</v-icon>
								</div>

								<p class="field-label">Process name</p>
								<p class="field-value">{{ getProcessField(row, 'name') }}</p>

								<p class="field-label">Owner</p>
								<p class="field-value">{{ getProcessField(row, 'owner') }}</p>

								<p class="field-label">Input</p>
								<p class="field-value">{{ getProcessField(row, 'input') }}</p>

								<p class="field-label">Output</p>
								<p class="field-value">{{ getProcessField(row, 'output') }}</p>
							</div>
						</draggable>
					</template>
				</v-card>
				<div v-if="activeStep === 'step2' && hasSavedProcesses" align="center" class="mt-5" >  
					<v-btn outlined color="primary" to="/risk/isoprocess/fullprocessview"><v-icon small left>fas fa-eye</v-icon>View Full Details</v-btn>
				</div>

				<!-- Step 2 -->
				<transition name="fade">
					<v-card v-if="activeStep === 'step2'" class="process-section pa-5 mt-10" outlined>
						<h2 class="text-center primary--text mb-5">Define Your Process</h2>

						<!-- One card per process entry -->
						<v-card
							v-for="(process, pIndex) in processes"
							:key="'process-' + pIndex"
							class="process-entry-card pa-4 mb-8"
							outlined
						>
							<div class="d-flex align-center justify-space-between mb-2" v-if="processes.length > 1">
								<span class="process-entry-label">Process {{ pIndex + 1 }}</span>
								<v-btn icon small @click="removeProcess(pIndex)" title="Remove this process">
									<v-icon small color="error">fas fa-trash</v-icon>
								</v-btn>
							</div>

							<div style="display: flex;">
								<v-text-field
									v-model="process.name"
									label="1. Process Name"
									dense
									outlined
									class="mr-5"
								/>
								<v-text-field
									v-model="process.owner"
									label="2. Process Owner"
									dense
									outlined
									class="mr-5"
								/>
								<v-select
									v-model="process.type"
									:items="processTypeOptions"
									item-text="text"
									item-value="value"
									label="3. Process Type"
									dense
									outlined
								/>
							</div>

							<v-text-field
									v-model="process.short"
									label="4. Description of the Process:"
									dense
									outlined
									class="mr-5"
								/>
							<p class="text-left mb-4" style="font-size: 14px">
								<i><v-icon small>fas fa-circle-info</v-icon> Add at least 3 steps describing this process in order, up to a maximum of 10.</i>
							</p>

							<div class="description-grid">
								<div
									v-for="(step, index) in process.description"
									:key="'desc-' + pIndex + '-' + index"
									class="chevron-box"
								>
									<div class="chevron-inner">
										<v-textarea
											v-model="process.description[index]"
											flat
											solo
											dense
											hide-details
											rows="3"
											no-resize
											class="chevron-textarea"
											:placeholder="'Describe step ' + (index + 1)"
										></v-textarea>
										<v-btn
											v-if="index >= 3"
											icon
											x-small
											class="step-remove-btn"
											@click="removeDescriptionStep(process, index)"
											title="Remove step"
										>
											<v-icon x-small>fas fa-xmark</v-icon>
										</v-btn>
									</div>
								</div>
							</div>

							<div class="mb-8">
								<v-btn
									small
									outlined
									color="primary"
									:disabled="process.description.length >= 10"
									@click="addDescriptionStep(process)"
								>
									+ Add Step ({{ process.description.length }}/10)
								</v-btn>
							</div>

							<div style="display: flex;">
								<v-text-field
									v-model="process.input"
									label="5. Input (What is the input and from Who)"
									dense
									outlined
									class="mr-5"
								/>
								<v-text-field
									v-model="process.output"
									label="6. Output (What is the output and who is the Receiver)"
									dense
									outlined
								/>
							</div>
						</v-card>

						<div align="right">
							<v-btn color="primary" outlined class="mr-5" @click="addProcess">+ Add Process</v-btn>
							<v-btn color="success" outlined @click="saveIsoProcess">Save</v-btn>
						</div>
					</v-card>
				</transition>

				<!-- Step 5 -->
				<transition name="fade">
					<v-card v-if="activeStep === 'step5'" class="process-section pa-5 mt-10" outlined>
						<h2 class="text-center primary--text mb-5">Management Review</h2>
						<template>
							<v-card style="display: flex; padding-top: 10px; padding-left: 100px; border: 1px solid #2d9b9b;">
								<p>Management Review <v-icon size="20" style="margin-right: 10px; margin-left: 10px;">fas fa-arrow-right-long</v-icon></p>
								<p>New Risk Matrix<v-icon size="20" style="margin-right: 10px; margin-left: 10px;">fas fa-arrow-right-long</v-icon></p>
								<p>Action Plan<v-icon size="20" style="margin-right: 10px; margin-left: 10px;">fas fa-arrow-right-long</v-icon></p>
								<p>Implementation<v-icon size="20" style="margin-right: 10px; margin-left: 10px;">fas fa-arrow-right-long</v-icon></p>
								<p>New Management Review</p>
							</v-card>
							<div>
								<h2 class="mt-10">Management Review - Context & Risk</h2>
								<h3 class="mt-5">1. Changes in Context</h3>
								<p>Have there been any significant changes in our external or internal context that may affect our management system? Consider changes in the 
									market, business ecosystem, customers, suppliers, regulations, technology, processes, organisation, and resources.</p>

								<div v-if="savedManagementData && !editingContext" class="saved-process-card ma-3">
									<div style="display: flex;">
										<div style="margin-left: 1.2%;">
											<p class="manage-title"><b>Date:</b></p>
											<p>{{ (savedManagementData.Timestamp).substring(0, 10) }}</p>
										</div>
										<div style="margin-left: 40%;">
											<p class="manage-title"><b>Persons Present:</b></p>
											<p>{{ savedManagementData.Person }}</p>
										</div>
									</div>

									<div class="text-block">
										<p class="manage-title"><b>Notes:</b></p>
										<p>{{ savedManagementData.Note }}</p>
									</div>
			
									<div align="right" class="mb-6 mr-6 mt-3">
										<v-btn class="primary" @click="editContext()"><v-icon small left>fas fa-pen</v-icon>Edit</v-btn>
									</div>
								</div>
								<div v-else style="border: 1px solid #2d9b9b; border-radius: 5px; margin: 10px; padding-top: 20px; padding-left: 20px;">
									<div style="display: flex;">
										<v-text-field v-model="date" type="date" label="Date" dense outlined class="mr-5" />
										<v-text-field v-model="person" label="Persons Present" dense outlined class="mr-5" />
									</div>
									<v-text-field v-model="notes" label="Notes" dense outlined class="mr-5" />
									<div align="right" class="mb-6 mr-6">
										<v-btn v-if="editingContext" outlined color="error" class="mr-3" @click="cancelEditContext()">Cancel</v-btn>
										<v-btn class="primary" @click="editingContext ? updateContext() : saveContext()">
											{{ editingContext ? 'Update' : 'Save' }}
										</v-btn>
									</div>
								</div>

								<div style="display: flex;" class="mt-5">
									<h3>2. Review of risks and opportunities</h3>
									<v-btn class="ml-5" color="primary" outlined :to="'/risk/riskpage#top'">Risk Matrix</v-btn>
								</div>
								<p>Does the current risk assessment still reflect our business? <br>
									Review the risk matrix and identify: <br>
									<v-icon>fas fa-</v-icon> New risks or opportunities <br>
									2. Changes in existing risks <br>
									3. Risks that are no longer relevant <br>
									4. Changes in risk levels or priorities.</p>
								
								<div style="display: flex;" class="mt-5">
									<h3>3. Effect of mitigation actions</h3>
									<v-btn style="margin-right: 40px;" class="ml-5" color="primary" outlined :to="'/risk/riskpage#actionplan'">Action Plan</v-btn>
								</div>
								<p>What actions have been implemented, and have they changed our risk exposure as intended? Review completed, ongoing, and overdue 
									actions, and assess whether the actions have effectively reduced the identified risks.</p>

								<h2 class="mt-10">Output of the Management Review</h2>
								<p>Based on the review, a new risk matrix should be established for the coming period, reflecting the organisation's current risks and opportunities. 
									A corresponding action plan should be created for the identified risks requiring mitigation or further action..</p>
							</div>
						</template>
					</v-card>
				</transition>
			</v-col>
		</v-row>
		<v-dialog v-model="dialogSteps" max-width="1000px">
			<v-card >
				<v-card-actions>
					<v-spacer />
					<v-btn icon color="primary" @click="dialogSteps = false">
						<v-icon>fas fa-times</v-icon>
					</v-btn>
				</v-card-actions>
				<template v-if="selectedProcess">
					<v-card-title class="steps-dialog-title">
						<h3 class="mb-1">Process Name - {{ getProcessField(selectedProcess, 'name') || 'Process' }}</h3>
						<span class="steps-dialog-subtitle">
							Owner: {{ getProcessField(selectedProcess, 'owner') || '—' }}
						</span>
					</v-card-title>
					<v-card-text>
						<template v-if="selectedProcessSteps.length > 0">
							<p class="field-label text-center mb-3">Process Steps</p>
							<div class="description-grid dialog-steps-grid">
								<div
									v-for="(step, sIndex) in selectedProcessSteps"
									:key="'dialog-step-' + sIndex"
									class="chevron-box dialog-step-box"
								>
									<div class="chevron-inner">
										<span class="step-number">{{ sIndex + 1 }}</span>
										<p class="step-text-value">{{ step }}</p>
									</div>
								</div>
							</div>
						</template>
						<p v-else class="text-center grey--text">
							No steps have been added for this process yet.
						</p>
					</v-card-text>
				</template>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import draggable from "vuedraggable"
import LoadingState from "../consts/LoadingState"
import { riskAPI } from "../api/csr/risk"

export default {
	components: {
		draggable,
	},
	data() {
		return {
			activeStep: null,
			processTypeOptions: [
				{ text: "Main Process", value: "main" },
				{ text: "Sub Process", value: "sub" },
			],
			processes: [this.newProcessTemplate()],
			savedMainProcesses: [],
			savedSubProcesses: [],
			reorderingMain: false,
			reorderingSub: false,
			savingMainOrder: false,
			savingSubOrder: false,
			mainOrderBackup: [],
			subOrderBackup: [],
			dialogSteps: false,
			selectedProcess: null,
			date: "",
			person: "",
			notes: "",
			savedManagementData: null,
			editingContext: false
		}
	},
	computed: {
		hasSavedProcesses() {
			return this.savedMainProcesses.length > 0 || this.savedSubProcesses.length > 0
		},
		selectedProcessSteps() {
			return this.getSteps(this.selectedProcess)
		},
	},
	mounted() {
		this.viewProcessData()
		this.viewManagementData()
	},
	methods: {
		newProcessTemplate() {
			return {
				name: "",
				owner: "",
				type: null,
				short: "",
				description: ["", "", ""],
				input: "",
				output: "",
			}
		},
		addProcess() {
			this.processes.push(this.newProcessTemplate())
		},
		removeProcess(index) {
			if (this.processes.length > 1) {
				this.processes.splice(index, 1)
			}
		},
		addDescriptionStep(process) {
			if (process.description.length < 10) {
				process.description.push("")
			}
		},
		removeDescriptionStep(process, index) {
			if (process.description.length > 3) {
				process.description.splice(index, 1)
			}
		},
		handleWorkflowStepClick(step) {
			this.activeStep = this.activeStep === step ? null : step

			if (step === "step1" && this.activeStep === "step1") {
				this.$router.push({
					path: "/team/settings",
					query: { scrollTo: "companyPolicy" }
				})
			}
			if (step === "step3" && this.activeStep === "step3") {
				this.$router.push({
					path: "/risk/riskpage#top",
				})
			}
			if (step === "step4" && this.activeStep === "step4") {
				this.$router.push({
					path: "/risk/riskpage#actionplan",
				})
			}
		},
		isStepGrayed(step) {
			return this.activeStep !== null && this.activeStep !== step
		},
		saveIsoProcess() {
			riskAPI.saveProcess(this.processes)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully saved process!`,
						{ root: true }
					)
					this.viewProcessData()
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		viewProcessData(){
			riskAPI.viewProcess()
				.then(result => {
					const rows = (result && result.length > 0 && Array.isArray(result[0])) ? result[0] : []

					this.savedMainProcesses = rows.filter(row => this.getProcessType(row) === "main")
					this.savedSubProcesses = rows.filter(row => this.getProcessType(row) === "sub")

					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, null, { root: true })
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		getProcessType(row) {
			return String(row.Type ?? "").toLowerCase()
		},
		getProcessField(row, field) {
			const columnByField = {
				name: "Name",
				owner: "Owner",
				input: "Input",
				output: "Output",
			}
			const column = columnByField[field]
			return column && row[column] !== undefined && row[column] !== null ? row[column] : ""
		},
		getSteps(row) {
			if (!row) return []
			const raw = row.Description ?? row.Steps ?? null
			if (!raw) return []

			if (Array.isArray(raw)) {
				return raw.map(step => String(step || "").trim()).filter(step => step !== "")
			}

			if (typeof raw === "string") {
				try {
					const parsed = JSON.parse(raw)
					if (Array.isArray(parsed)) {
						return parsed.map(step => String(step || "").trim()).filter(step => step !== "")
					}
				} catch (e) {
					// not JSON, fall through to newline splitting
				}
				return raw.split("\n").map(step => step.trim()).filter(step => step !== "")
			}

			return []
		},

		// --- Steps popup ---
		openStepsDialog(row) {
			this.selectedProcess = row
			this.dialogSteps = true
		},

		// --- Reorder only ---
		startReorder(type) {
			if (type === "main") {
				this.mainOrderBackup = this.savedMainProcesses.map(row => ({ ...row }))
				this.reorderingMain = true
			} else {
				this.subOrderBackup = this.savedSubProcesses.map(row => ({ ...row }))
				this.reorderingSub = true
			}
		},
		cancelReorder(type) {
			if (type === "main") {
				this.savedMainProcesses = this.mainOrderBackup
				this.reorderingMain = false
			} else {
				this.savedSubProcesses = this.subOrderBackup
				this.reorderingSub = false
			}
		},
		saveOrder(type) {
			const rows = type === "main" ? this.savedMainProcesses : this.savedSubProcesses
			const payload = rows.map((row, index) => ({
				id: row.ID,
				name: row.Name,
				owner: row.Owner,
				input: row.Input,
				output: row.Output,
				order: index + 1,
			}))

			if (type === "main") this.savingMainOrder = true
			else this.savingSubOrder = true

			riskAPI.updateProcesses(type, payload)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully updated process order!`,
						{ root: true }
					)
					if (type === "main") this.reorderingMain = false
					else this.reorderingSub = false
					this.viewProcessData()
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
				.finally(() => {
					if (type === "main") this.savingMainOrder = false
					else this.savingSubOrder = false
				})
		},
		saveContext() {
			riskAPI.saveManagement(this.date, this.person, this.notes)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully saved!`,
						{ root: true }
					)
					this.viewManagementData() // reload so the data view replaces the form
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		viewManagementData() {
			riskAPI.getManagement()
				.then(data => {
					this.savedManagementData = (data && data[0] && data[0][0]) || null
					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, null, { root: true })
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		editContext() {
			this.date = (this.savedManagementData.Timestamp || "").substring(0, 10)
			this.person = this.savedManagementData.Person || ""
			this.notes = this.savedManagementData.Note || ""
			this.editingContext = true
		},
		cancelEditContext() {
			this.editingContext = false
			this.date = ""
			this.person = ""
			this.notes = ""
		},
		updateContext() {
			riskAPI.updateManagement(this.savedManagementData.ID, this.date, this.person, this.notes)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully updated!`,
						{ root: true }
					)
					this.editingContext = false
					this.date = ""
					this.person = ""
					this.notes = ""
					this.viewManagementData()
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
	},
}
</script>

<style scoped>
.steps-wrapper {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	position: relative;
	z-index: 2;
	padding: 0 5%;
	border: 2px solid #1e7c83;
	border-radius: 5px;
}

.step-container {
  text-align: center;
  max-width: 100px;
}

.step-circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.step-circle:hover {
  transform: translateY(-2px);
}

.step-text h5 {
  font-weight: 400;
  margin: 2px 0;
  line-height: 1.2;
}

.arrow-col {
	margin: 4% 0 0 3%;
}

.tutorial-grayed {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.step-active {
  transform: scale(1.05);
}

.clickable-step {
	cursor: pointer;
}

.clickable-step .step-circle {
	cursor: pointer;
}

.clickable-step .v-icon {
	cursor: pointer;
}

.process-section {
	margin-top: 20px;
	position: relative;
	z-index: 1;
}

.process-entry-card {
	border: 2px solid #2d9b9b !important;
	border-radius: 12px !important;
}

.process-entry-label {
	font-weight: 600;
	font-size: 13px;
	color: #666;
	letter-spacing: 1px;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
.description-grid {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 14px;
	margin-bottom: 16px;
}

.chevron-box {
	position: relative;
	height: 100px;
}

.chevron-inner {
	position: relative;
	background: #fff;
	border: 2px solid #2d9b9b;
	border-radius: 10px;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	padding: 4px;
	overflow: hidden;
}

.step-remove-btn {
	position: absolute;
	top: 2px;
	right: 2px;
}

.chevron-textarea {
	width: 100%;
	height: 100%;
}

.chevron-textarea >>> .v-input__slot {
	background: transparent !important;
	box-shadow: none !important;
	border: none !important;
}

.chevron-textarea >>> .v-text-field__details {
	display: none !important;
}

.chevron-textarea >>> textarea {
	color: #333;
	cursor: text;
	resize: none;
}

.chevron-textarea >>> textarea::placeholder {
	color: #999;
}

/* ---- Saved processes (view / reorder mode) ---- */
.saved-processes-section {
	margin-top: 20px;
	position: relative;
	z-index: 1;
}

.saved-section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 15px;
}

.saved-section-title {
	color: #1e7c83;
	font-weight: 600;
	margin-bottom: 0;
}

.drag-hint {
	font-size: 13px;
	color: #666;
	margin-bottom: 12px;
}

.saved-process-grid {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 10px;
}

.saved-process-card {
	position: relative;
	border: 2px solid #2d9b9b;
	border-radius: 12px;
	padding: 16px;
	background: #fff;
}

.saved-process-card.clickable-card {
	cursor: pointer;
	transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.saved-process-card.clickable-card:hover {
	box-shadow: 0 6px 14px rgba(45, 155, 155, 0.25);
	transform: translateY(-2px);
}

.saved-process-card.drag-enabled {
	padding-top: 30px;
	border-style: dashed;
}

.view-steps-hint {
	margin-top: 10px;
	font-size: 12px;
	font-weight: 600;
	color: #2d9b9b;
	display: flex;
	align-items: center;
	gap: 6px;
}

.drag-handle {
	position: absolute;
	top: 4px;
	left: 0;
	right: 0;
	display: flex;
	justify-content: center;
	color: #999;
	cursor: grab;
	padding: 2px 0;
}

.drag-handle:active {
	cursor: grabbing;
}

.drag-ghost {
	opacity: 0.4;
	background: #eafafa;
}

.saved-process-card .field-label {
	font-weight: 700;
	margin: 6px 0 0 0;
}

.saved-process-card .field-value {
	margin: 0 0 4px 0;
	word-break: break-word;
	color: #333;
}

/* ---- Steps popup ---- */
.steps-dialog-title {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	margin-top: -20px;
}

.steps-dialog-subtitle {
	font-size: 13px;
	color: #666;
	font-weight: 400;
}

.dialog-steps-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 14px;
}

.dialog-steps-grid .dialog-step-box {
    flex: 0 0 calc((100% - 56px) / 5);
    max-width: calc((100% - 56px) / 5);
}

.dialog-step-box .chevron-inner {
	align-items: flex-start;
	padding: 8px;
}

.step-number {
	position: absolute;
	top: 6px;
	left: 6px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: #2d9b9b;
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.step-text-value {
	margin: 0 0 0 26px;
	color: #333;
	line-height: 1.3;
	overflow-y: auto;
	max-height: 100%;
	width: 100%;
}

.text-block {
	background: #f9f9f9;
	border-radius: 6px;
	padding: 10px 14px;
}

.manage-title {
	margin: 0;
}

@media (max-width: 900px) {
	.description-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 1200px) {
	.saved-process-grid {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
}

@media (max-width: 900px) {
    .dialog-steps-grid .dialog-step-box {
        flex: 0 0 calc((100% - 28px) / 3);
        max-width: calc((100% - 28px) / 3);
    }
}

@media (max-width: 600px) {
    .dialog-steps-grid .dialog-step-box {
        flex: 0 0 calc((100% - 14px) / 2);
        max-width: calc((100% - 14px) / 2);
    }
}

@media (max-width: 420px) {
    .dialog-steps-grid .dialog-step-box {
        flex: 0 0 100%;
        max-width: 100%;
    }
}

@media (max-width: 768px) {
	.steps-wrapper {
		flex-direction: column;
		align-items: center;
		gap: 10px;
		padding: 15px;
	}
	
	.arrow-col {
		margin: 0;
		padding: 10px 0;
		width: auto;
		min-height: auto;
	}
	
	.arrow-col .v-icon {
		transform: rotate(90deg);
	}
	
	.step-text h5 {
		font-size: 1rem;
		min-height: auto;
	}
	
	.step-container {
		max-width: 200px;
	}

	.description-grid {
		grid-template-columns: 1fr;
	}

	.saved-process-grid {
		grid-template-columns: 1fr;
	}

	.saved-section-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}
}

@media (max-width: 1024px) and (min-width: 769px) {
	.step-text h5 {
		font-size: 0.8rem;
	}
	
	.step-circle {
		width: 70px;
		height: 70px;
	}
	
	.step-circle .v-icon {
		font-size: 30px !important;
	}
	
	.steps-wrapper {
		padding: 15px;
	}
}

@media (min-width: 1025px) {
	.steps-wrapper {
		padding: 10px 15px;
	}
	
	.arrow-col {
		padding: 0 4px;
	}
}
</style>